import { useState } from "react";

import { Alert, AlertKind, Button, FormField, Spinner } from "@prequel/shared-components";

import { FormState } from "../LoginCard";

import { usePasswordlessCode, usePasswordlessLogin } from "@/api/queries/auth";

type PasswordlessSubmittedCardProps = {
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
};

const PasswordlessSubmittedCard = ({ setFormState, email, setEmail }: PasswordlessSubmittedCardProps) => {
  const passwordlessLogin = usePasswordlessLogin();
  const passwordlessCode = usePasswordlessCode();
  const [code, setCode] = useState("");

  const logInWithCode = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    passwordlessCode.mutate({ email, code });
  };

  return (
    <form onSubmit={logInWithCode} className="space-y-4">
      <FormField
        id="email"
        key="email"
        type="email"
        placeholder="you@yourcompany.com"
        value={email}
        label="Email"
        onChangeHandler={setEmail}
        required
        disabled
        autoFocus
      />

      {passwordlessLogin.isPending && (
        <Button
          className="w-full"
          submit
          disabled
          text={
            <div className="flex space-x-1">
              <Spinner.Inline className="mr-3 text-white" />
              <span>Sending link...</span>
            </div>
          }
        />
      )}

      {passwordlessLogin.isSuccess && (
        <>
          <Alert kind={AlertKind.SUCCESS}>
            <span className="leading-relaxed">
              {"Check your email! We've sent you a one-time code to enter below."}
            </span>
          </Alert>
        </>
      )}

      {!passwordlessLogin.isPending && (
        <>
          <FormField
            id="auth_code"
            key="auth_code"
            type="number"
            label="One-time code"
            value={code}
            placeholder="123456"
            onChangeHandler={(c) => setCode(c)}
            required
          />
          <Button
            className="w-full"
            submit
            disabled={passwordlessCode.isPending}
            text={
              <div className="flex space-x-1">
                {passwordlessCode.isPending ? (
                  <>
                    <Spinner.Inline className="mr-1 text-white" />
                    <span>Submitting one-time code...</span>
                  </>
                ) : (
                  <span>Submit one-time code</span>
                )}
              </div>
            }
          />
        </>
      )}

      {passwordlessCode.isError && (
        <>
          <Alert>An error occurred while using passwordless login.</Alert>
          <div className="flex justify-center text-sm text-gray-500">
            <a
              className="
                cursor-pointer text-primary-600
                hover:underline
              "
              onClick={() => setFormState("NOTHING_SELECTED")}
            >
              Back to other options
            </a>
          </div>
        </>
      )}
    </form>
  );
};

export default PasswordlessSubmittedCard;
